/**
 * Single source of truth for environment configuration and feature flags.
 *
 * The only value most deployments change is {@link config.gatewayBaseUrl}. An
 * empty base means same origin, which is the staging setup where the reverse
 * proxy serves both the app and the API. Keep this module free of secrets: it
 * ships to the browser as plain source.
 */

export interface FeatureFlags {
  /** Ask the OS to keep storage persistent so app data is not evicted. */
  requirePersistentStorage: boolean;
  /** Emit verbose console diagnostics. Never log secret material regardless. */
  devLogging: boolean;
  /**
   * Allow the legacy developer bearer-token fallback: resolve a token from
   * localStorage or config.devToken when no real session is active, and show
   * the console-token disclosure on the login screen. Dev builds only — in a
   * production build a stale stored token must never keep the client looking
   * authenticated after a real session expires, or the boot gate can never
   * route back to the login screen.
   */
  devTokenFallback: boolean;
}

export interface AppConfig {
  /**
   * Base URL of the gateway REST API with no trailing slash. Empty string means
   * same origin, so `/v1/...` paths resolve against the current page's origin.
   * Set to an absolute origin only to target a gateway on a different host.
   */
  gatewayBaseUrl: string;
  /**
   * Base URL the browser reaches the swap service under, with no trailing slash.
   * The swap service is a separate process the gateway or reverse proxy fronts;
   * the browser never calls it directly by FFI. Empty string means same origin,
   * so the swap paths (`/v1/swap/...`) resolve against the current page's origin
   * and must be routed to the swap service by the reverse proxy (the local Vite
   * dev server does this rewrite for development). Set to an absolute origin only
   * when the swap service is fronted on a different host, in which case the
   * app's Content-Security-Policy connect-src and the swap service's CORS must
   * both allow that origin. Kept distinct from `gatewayBaseUrl` so swap traffic
   * and core wallet traffic can be fronted independently without assuming they
   * share one origin.
   */
  swapApiBaseUrl: string;
  /** Optional bearer token baked in for local development. Empty ships nothing. */
  devToken: string;
  /** localStorage key for the legacy developer bearer token fallback. */
  tokenStorageKey: string;
  /** localStorage key the refresh token is persisted under to resume a session. */
  refreshStorageKey: string;
  /** localStorage key the non-secret session metadata JSON is persisted under. */
  sessionMetaStorageKey: string;
  /** localStorage key the install-scoped device fingerprint is persisted under. */
  deviceStorageKey: string;
  /**
   * Fallback organization id, kept for deployments that route to a fixed shared
   * org. The default login flow is self-service: the client sends the
   * self-service sentinel and records the personal org the gateway returns, so
   * this value is not used on the normal sign-in path. It is not a trust
   * boundary; the gateway re-resolves authorization from the members table.
   */
  defaultOrgId: string;
  /** Provider names the login view renders a button for. */
  oauthProviders: string[];
  /** Per-request timeout for gateway calls, in milliseconds. */
  requestTimeoutMs: number;
  /** Maximum automatic retries for idempotent reads. */
  maxRetries: number;
  /** Build-time feature toggles. */
  flags: FeatureFlags;
}

/**
 * Active configuration. The default empty base URL means same origin: the app
 * and the gateway API are served from one origin behind the staging reverse
 * proxy, so `/v1/...` requests resolve against the current page's origin with no
 * cross-origin fetch and no CORS.
 */
export const config: AppConfig = {
  gatewayBaseUrl: '',
  // Same origin by default: the reverse proxy routes `/v1/swap/...` to the swap
  // service, so the browser reaches it without a cross-origin fetch and the
  // strict same-origin CSP holds. Point this at an absolute origin only when the
  // swap service is fronted separately, and update the CSP connect-src and the
  // service's CORS to match.
  swapApiBaseUrl: '',
  devToken: '',
  tokenStorageKey: 'pmw.token',
  refreshStorageKey: 'pmw.refresh',
  sessionMetaStorageKey: 'pmw.session',
  deviceStorageKey: 'pmw.device',
  // Fallback shared-org id for deployments that pin one. The default web login
  // is self-service and records the personal org the gateway returns, so this is
  // not used on the normal sign-in path. The gateway re-resolves the caller's
  // authorization from the members table regardless.
  defaultOrgId: '00000000-0000-0000-0000-000000000001',
  // Each provider must be registered on the gateway; an unregistered one returns
  // a 400 the login view surfaces by disabling that provider's button.
  // ORDER IS THE RENDER ORDER — the login view maps this array straight to cards.
  oauthProviders: [
    'google',
    // Hidden for now. Kept (commented, not removed) — uncomment to restore.
    // 'kakao',
    // Not offered yet. Kept (commented, not removed) — uncomment to restore.
    // 'discord',
    // 'twitter',
    // 'apple',
  ],
  requestTimeoutMs: 15000,
  maxRetries: 2,
  flags: {
    requirePersistentStorage: true,
    devLogging: true,
    devTokenFallback: import.meta.env.DEV,
  },
};

/**
 * Join the configured base URL with an API path.
 *
 * @param path Path beginning with a slash, for example "/v1/wallets".
 * @returns The absolute URL to fetch.
 */
export function apiUrl(path: string): string {
  const base = config.gatewayBaseUrl.replace(/\/+$/, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${base}${suffix}`;
}

/**
 * Join the configured swap base URL with a swap API path. Distinct from
 * {@link apiUrl} so swap traffic can target a separately fronted swap service
 * without assuming it shares the gateway origin. With the default empty base
 * this returns the same-origin path, which the reverse proxy routes to the swap
 * service.
 *
 * @param path Path beginning with a slash, for example "/v1/swap/cross-chain/quotes".
 * @returns The absolute URL to fetch.
 */
export function swapUrl(path: string): string {
  const base = config.swapApiBaseUrl.replace(/\/+$/, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${base}${suffix}`;
}
